import { useMemo } from "react";
import { useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import Skeleton from "react-loading-skeleton";
import { classNames, useAppConfig } from "@ct-react/core";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import { extractCmsContent, Image } from "@shared/graphql";
import { article, pages } from "../../i18n/sharable-defs";
import { SkeletonableProps } from "../../tools/components";
import SeoMetas from "../../components/layout/seo-metas";
import PageHero from "../../components/layout/page-elements/page-hero";
import { Wrapper } from "../../components/layout/wrapper";
import RatioImage from "../../components/medias/ratio-image";
import heroPath from "../../assets/bg-images/hero-news.webp";
import "./index.scss";

const NEWS_LIST_GQL_DATA = gql`
  query NewsPages($templateId: UUID!) {
    news: cmsPages(templateId: $templateId, asSummary: true) {
      id createdAt data
    }
  }
`;

type News = {
  href: string;
  title: string;
  lead?: string;
  image: Image;
  createdAt: string;
}

const NewsCard = ({ loading, className, data }: SkeletonableProps<News>) => {

  const intl = useIntl();
  const wrapperClasses = classNames("news-card", className);

  if (loading || !data)
    return (
      <article className={wrapperClasses}>
        <Skeleton className="card-picture" style={{ aspectRatio: "16/9" }} />
        <div className="card-resume">
          <Skeleton containerClassName="date" width={225} />
          <h2>
            <Skeleton className="title" width="100%" />
            <Skeleton className="title" width="35%" />
          </h2>
          <p><Skeleton width="100%" count={2} /></p>
        </div>
      </article>);

  return (
    <article className={wrapperClasses}>
      <LocaleLink to={data.href}>
        <RatioImage className="card-picture" source={data.image.assets} ratio="16/9" />
        <div className="card-resume">
          <span className="date">
            {intl.formatMessage(article.published, {
              date: intl.formatDate(data.createdAt, { dateStyle: "medium", timeStyle: "short" }) })
            }
          </span>
          <h2>{data.title}</h2>
          {!!data.lead && <p>{data.lead}</p>}
        </div>
      </LocaleLink>
    </article>);

}

const NewsIndexPage = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { options: { cmsNewsTemplateId: templateId } } = useAppConfig();

  const { loading, data } = useQuery<{ news: any[] }>(NEWS_LIST_GQL_DATA, {
    ssr: true,
    variables: { templateId }
  });

  const items = useMemo<News[] | undefined>(() => {
    if (!data?.news) return undefined;
    return [ ...data.news ]
      .sort(({ createdAt: a }, { createdAt: b }) => new Date(b).getTime() - new Date(a).getTime())
      .map(({ data, ...rest }) => ({ ...rest, data: extractCmsContent(data, locale) }))
      .map(({ id, data, createdAt, publishedAt }) => ({
        id,
        href: `/agency/news/${id}`,
        title: data.title,
        ...(!!data.lead )&& { lead: data.lead },
        image: data.image,
        createdAt,
        publishedAt
      }));
  }, [ data, locale ]);

  return (
    <>
      <SeoMetas title={`${intl.formatMessage(pages.news)} | EV Real Estate`} />
      <PageHero title={intl.formatMessage(pages.news)}
                background={heroPath} />

      <Wrapper.Centered as="main" className="news content">
        {(items || Array.from({ length: 4 })).map((news: any, index: number) => (
          <NewsCard key={news?.id || index} loading={loading} data={news} />
        ))}
      </Wrapper.Centered>
    </>);

}

export default NewsIndexPage;
